/**
 * CandidateProfileDrawer.tsx — two-pane candidate profile for the local talent pool.
 *
 * LEFT pane  = the profile: header (name/socials/location), company + education chips, and a scrolling
 *              body — signals, summary, stat cards, an experience timeline (consecutive same-company
 *              roles grouped), education, and a skill map. (Ported from the Juicebox replica's
 *              EsProfileDrawer, mapped to the crypto-lever `_source` field names.)
 * RIGHT pane = three tabs:
 *   • Criteria      — per-criterion 3-state verdict (yes/maybe/no) + explanation + match rate (Gemini).
 *   • Résumé(s)     — latest résumé inline preview + every application's résumé (presigned S3).
 *   • Applications  — the live Lever recruiting history (stages, screening Q&A, interviews, feedback…).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
    X,
    MapPin,
    Building2,
    GraduationCap,
    Star,
    ChevronRight,
    ChevronLeft,
    FileText,
    ThumbsUp,
    Ban,
    Sparkles,
    ExternalLink,
    Globe,
    Rocket,
    Loader2,
} from 'lucide-react';
import {
    getCandidate,
    getCandidateHistory,
    getResumeUrl,
    getOppResumeUrl,
    getResumeBytes,
    type CandidateResult,
    type CriteriaResult,
    type CandidateHistory,
    type ApplicationEntry,
    type ResumeLink,
    type ExperienceEntry,
} from '@/lib/talentPool.api';
import { getApiErrorMessage } from '@/lib/http';
import { Avatar, CompanyLogo } from '@/app/agent/[id]/artifacts/types/search-strategy/primitives';
import { LinkedinIcon, GithubIcon } from './brandIcons';

// ── date / duration helpers (engine `_source`: date_from/date_to "YYYY"|"YYYY-MM", active_experience=1) ──
const NOW = new Date();
const yearNum = (s?: string | number | null): number | undefined => {
    if (s == null) return undefined;
    const n = parseInt(String(s).slice(0, 4), 10);
    return Number.isFinite(n) ? n : undefined;
};
const monthNum = (s?: string | null): number => {
    const m = String(s ?? '').match(/^\d{4}-(\d{2})/);
    return m ? parseInt(m[1], 10) : 1;
};
const isCurrent = (e: ExperienceEntry) => e.active_experience === 1;
const startY = (e: ExperienceEntry) => e.date_from_year ?? yearNum(e.date_from);
const endY = (e: ExperienceEntry) => e.date_to_year ?? yearNum(e.date_to);
function monthsOf(e: ExperienceEntry): number {
    const sy = startY(e);
    if (!sy) return 0;
    const sm = monthNum(e.date_from);
    const ey = isCurrent(e) ? NOW.getFullYear() : endY(e) ?? NOW.getFullYear();
    // only borrow today's month for genuinely current roles; a past role with only a year ends mid-data, not now
    const em = isCurrent(e) ? NOW.getMonth() + 1 : e.date_to ? monthNum(e.date_to) : 12;
    return Math.max(0, (ey - sy) * 12 + (em - sm));
}
function ym(months?: number | null): string {
    if (!months || months < 1) return '—';
    const y = Math.floor(months / 12);
    const m = months % 12;
    if (y && m) return `${y} yr${y > 1 ? 's' : ''} ${m} mo`;
    if (y) return `${y} yr${y > 1 ? 's' : ''}`;
    return `${m} mo`;
}
function fmtRange(e: ExperienceEntry): string {
    const s = startY(e);
    const end = isCurrent(e) ? 'Present' : endY(e) ?? '';
    if (!s && !end) return '';
    return [s, end].filter(Boolean).join(' – ');
}
const d8 = (s?: string | null) => (s ? String(s).slice(0, 10) : '');

// ── 3-state verdict presentation (mirrors the replica's yes/maybe/no) ──
const VERDICT: Record<CriteriaResult['verdict'], { icon: typeof ThumbsUp; color: string; label: string }> = {
    yes: { icon: ThumbsUp, color: '#2e8b4f', label: 'Met' },
    maybe: { icon: ThumbsUp, color: '#d4a84b', label: 'Partial' },
    no: { icon: Ban, color: '#d14b4b', label: 'Not met' },
};

/** group consecutive experience entries at the same company */
function groupExp(exp: ExperienceEntry[]): { company?: string; domain?: string; roles: ExperienceEntry[] }[] {
    const groups: { company?: string; domain?: string; roles: ExperienceEntry[] }[] = [];
    for (const e of exp) {
        const last = groups[groups.length - 1];
        if (last && last.company === e.company_name) last.roles.push(e);
        else groups.push({ company: e.company_name, domain: e.company_domain, roles: [e] });
    }
    return groups;
}

/** derived "signal" chips */
function signals(c: CandidateResult): { label: string; icon: typeof Star; color: string }[] {
    const out: { label: string; icon: typeof Star; color: string }[] = [];
    if (c.job_hopper || (c.avg_tenure_months != null && c.avg_tenure_months < 24))
        out.push({ label: 'Likely to switch', icon: Star, color: '#d4a84b' });
    const countries = new Set(
        (c.experience || []).map((e) => (e.location || '').split(',').pop()?.trim()).filter(Boolean),
    );
    if (countries.size >= 2) out.push({ label: 'International exp.', icon: Globe, color: '#487ca5' });
    if ((c.num_jobs ?? 0) >= 6) out.push({ label: 'Broad path', icon: Rocket, color: '#0d1830' });
    return out;
}

const favicon = (domain?: string) => (domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=64` : undefined);

type TabId = 'criteria' | 'resumes' | 'applications';

export function CandidateProfileDrawer({
    summary,
    onClose,
    criteria = [],
    criteriaLabels = [],
    onPrev,
    onNext,
    isFirst,
    isLast,
    index,
    total,
}: {
    summary: CandidateResult;
    onClose: () => void;
    criteria?: string[];
    criteriaLabels?: string[];
    onPrev?: () => void;
    onNext?: () => void;
    isFirst?: boolean;
    isLast?: boolean;
    index?: number;
    total?: number;
}) {
    const [full, setFull] = useState<CandidateResult | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [history, setHistory] = useState<CandidateHistory | null>(null);
    const [historyLoading, setHistoryLoading] = useState(true);
    const [resume, setResume] = useState<ResumeLink | null>(null);
    const [resumeState, setResumeState] = useState<'loading' | 'ready' | 'none'>('loading');
    const scored = Array.isArray(summary.criteria_results) && summary.criteria_results.length > 0;
    const [tab, setTab] = useState<TabId>(scored ? 'criteria' : 'applications');

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setHistoryLoading(true);
        setError(null);
        setHistory(null);
        // preload the latest résumé so the Résumé(s) tab is instant when opened
        setResume(null);
        setResumeState('loading');
        getResumeUrl(summary.id)
            .then((r) => {
                if (alive) {
                    setResume(r);
                    setResumeState('ready');
                }
            })
            .catch(() => alive && setResumeState('none'));
        getCandidate(summary.id)
            .then((c) => alive && setFull(c))
            .catch((e) => alive && setError(getApiErrorMessage(e, 'Could not load profile')))
            .finally(() => alive && setLoading(false));
        getCandidateHistory(summary.id)
            .then((h) => alive && setHistory(h))
            .catch(() => alive && setHistory(null))
            .finally(() => alive && setHistoryLoading(false));
        return () => {
            alive = false;
        };
    }, [summary.id]);

    // carry the scored criteria from the result row onto the enriched profile (the engine doc isn't scored)
    const c: CandidateResult = useMemo(
        () => ({
            ...(full ?? {}),
            ...summary,
            ...(full ?? {}),
            id: summary.id,
            criteria_results: summary.criteria_results,
            match_rate: summary.match_rate,
            criteria_summary: summary.criteria_summary,
        }),
        [full, summary],
    );

    const name = c.full_name || 'Unknown';
    const activeCompany = c.experience?.find(isCurrent)?.company_name || c.experience?.[0]?.company_name;
    const activeDomain = c.experience?.find(isCurrent)?.company_domain || c.experience?.[0]?.company_domain;
    const school = c.education?.[0];
    const sig = signals(c);
    const skills = (c.skills?.length ? c.skills.map((s) => s.skill) : c.skills_canonical?.length ? c.skills_canonical : c.inferred_skills ?? []).filter(Boolean);

    return (
        <div className="fixed inset-0 z-[1100] flex justify-end">
            <button type="button" aria-label="Close" className="flex-1 bg-black/30" onClick={onClose} />
            <aside className="flex h-full w-[min(1180px,98vw)] flex-col bg-[var(--surface-primary)] shadow-xl">
                {/* toolbar */}
                <div className="flex h-12 shrink-0 items-center gap-1 border-b border-[var(--border-muted)] px-4">
                    <button
                        type="button"
                        onClick={onPrev}
                        disabled={!onPrev || isFirst}
                        className="rounded p-1 text-[var(--text-tertiary)] hover:bg-[var(--surface-secondary)] disabled:opacity-30"
                        aria-label="Previous"
                    >
                        <ChevronLeft size={18} />
                    </button>
                    <button
                        type="button"
                        onClick={onNext}
                        disabled={!onNext || isLast}
                        className="rounded p-1 text-[var(--text-tertiary)] hover:bg-[var(--surface-secondary)] disabled:opacity-30"
                        aria-label="Next"
                    >
                        <ChevronRight size={18} />
                    </button>
                    <span className="ml-1 text-[14px] font-medium text-[var(--text-secondary)]">
                        Candidate
                        {typeof index === 'number' && total ? <span className="ml-1 font-normal text-[var(--text-tertiary)] tabular-nums">{index + 1} / {total}</span> : null}
                    </span>
                    <div className="flex-1" />
                    <button type="button" onClick={onClose} className="rounded p-1 text-[var(--text-tertiary)] hover:text-[var(--text-primary)]" aria-label="Close">
                        <X size={18} />
                    </button>
                </div>

                <div className="flex min-h-0 flex-1">
                    {/* ════ LEFT: profile ════ */}
                    <div className="flex min-w-0 flex-1 flex-col border-r border-[var(--border-muted)]" style={{ flexBasis: '55%' }}>
                        {/* header */}
                        <div className="flex shrink-0 items-start gap-3 px-6 pt-4 pb-3">
                            <Avatar name={name} size={48} />
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <span className="truncate text-[18px] font-semibold text-[var(--text-primary)]">{name}</span>
                                    <div className="flex items-center gap-2">
                                        {c.linkedin_url && (
                                            <a href={c.linkedin_url} target="_blank" rel="noreferrer" aria-label="LinkedIn">
                                                <LinkedinIcon size={17} />
                                            </a>
                                        )}
                                        {c.github_url && (
                                            <a href={c.github_url} target="_blank" rel="noreferrer" aria-label="GitHub">
                                                <GithubIcon size={17} />
                                            </a>
                                        )}
                                        {c.portfolio_url && (
                                            <a href={c.portfolio_url} target="_blank" rel="noreferrer" aria-label="Website" className="text-[var(--text-tertiary)]">
                                                <ExternalLink size={15} />
                                            </a>
                                        )}
                                    </div>
                                </div>
                                {(c.active_experience_title || c.headline) && (
                                    <div className="mt-0.5 truncate text-[13px] text-[var(--text-secondary)]">{c.active_experience_title || c.headline}</div>
                                )}
                                <div className="mt-0.5 flex items-center gap-2 text-[12px] text-[var(--text-tertiary)]">
                                    {(c.location_city_canonical || c.location_full) && (
                                        <span className="inline-flex items-center gap-1">
                                            <MapPin size={12} /> {c.location_city_canonical || c.location_full}
                                            {c._timezone ? ` · ${c._timezone}` : ''}
                                        </span>
                                    )}
                                    {c.open_to_work && (
                                        <span className="rounded bg-[var(--surface-tertiary)] px-1.5 py-0.5 text-[11px] font-medium text-[#2e8b4f]">Open to work</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* company + education chips */}
                        {(activeCompany || school) && (
                            <div className="flex shrink-0 items-center gap-4 overflow-hidden px-6 pb-3 text-[13px] text-[var(--text-secondary)]">
                                {activeCompany && (
                                    <span className="inline-flex min-w-0 items-center gap-1.5">
                                        <CompanyLogo name={activeCompany} src={favicon(activeDomain)} size={18} />
                                        <span className="truncate">{activeCompany}</span>
                                    </span>
                                )}
                                {school?.institution_name && (
                                    <span className="inline-flex min-w-0 items-center gap-1.5">
                                        <GraduationCap size={15} className="text-[var(--text-tertiary)]" />
                                        <span className="truncate">
                                            {school.institution_name}
                                            {school.date_to_year ? ` ${school.date_to_year}` : ''}
                                        </span>
                                    </span>
                                )}
                            </div>
                        )}

                        {/* scrolling body */}
                        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-10 pt-1">
                            {loading && (
                                <div className="flex items-center gap-2 py-6 text-[13px] text-[var(--text-muted)]">
                                    <Loader2 size={14} className="animate-spin" /> Loading profile…
                                </div>
                            )}
                            {error && <div className="py-4 text-[13px] text-[var(--fg-danger)]">{error}</div>}

                            {/* signals + contact reveal */}
                            <div className="flex flex-col gap-2 py-2">
                                {sig.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5">
                                        {sig.map((s) => {
                                            const Icon = s.icon;
                                            return (
                                                <span
                                                    key={s.label}
                                                    className="inline-flex items-center gap-1.5 rounded-md bg-[var(--surface-secondary)] px-2.5 py-1 text-[12px] text-[var(--text-secondary)]"
                                                >
                                                    <Icon size={13} style={{ color: s.color }} />
                                                    {s.label}
                                                </span>
                                            );
                                        })}
                                    </div>
                                )}
                                {(c.emails?.length || c.phone) && (
                                    <div className="text-[12px] text-[var(--text-secondary)]">
                                        {[...(c.emails ?? []), c.phone].filter(Boolean).join(' · ')}
                                    </div>
                                )}
                            </div>

                            {c.summary && (
                                <div className="mb-3">
                                    <ReadMore text={c.summary} className="text-[13px] leading-relaxed text-[var(--text-secondary)]" />
                                </div>
                            )}

                            {/* stat cards */}
                            <div className="mb-4 grid grid-cols-3 gap-2">
                                <StatCard k="Avg tenure" v={ym(c.avg_tenure_months)} />
                                <StatCard k="Current role" v={ym(c.current_role_months)} />
                                <StatCard k="Total exp." v={ym(c.total_experience_duration_months)} />
                            </div>

                            {/* experience timeline */}
                            {Array.isArray(c.experience) && c.experience.length > 0 && (
                                <section className="mb-5">
                                    <SectionTitle>Experience</SectionTitle>
                                    <div className="flex flex-col gap-5">
                                        {groupExp(c.experience).map((g, gi) => (
                                            <ExperienceGroup key={gi} group={g} />
                                        ))}
                                    </div>
                                </section>
                            )}

                            {/* education */}
                            {Array.isArray(c.education) && c.education.length > 0 && (
                                <section className="mb-5">
                                    <SectionTitle>Education</SectionTitle>
                                    <div className="flex flex-col gap-3">
                                        {c.education.map((e, i) => (
                                            <div key={i} className="flex items-start gap-3">
                                                <GraduationCap size={18} className="mt-0.5 shrink-0 text-[var(--text-tertiary)]" />
                                                <div className="min-w-0">
                                                    <div className="text-[13px] font-medium text-[var(--text-primary)]">{e.institution_name}</div>
                                                    <div className="text-[12px] text-[var(--text-secondary)]">
                                                        {[e.degree, e.field_of_study, e.date_to_year].filter(Boolean).join(' · ')}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {c.certifications && c.certifications.length > 0 && (
                                        <div className="mt-3">
                                            <div className="mb-1 text-[12px] text-[var(--text-secondary)]">Certifications</div>
                                            <div className="flex flex-col gap-1 text-[12px]">
                                                {c.certifications.map((cert, i) => (
                                                    <div key={i}>
                                                        <span className="font-medium text-[var(--text-primary)]">{cert.title}</span>
                                                        {cert.issuer ? <span className="text-[var(--text-tertiary)]"> · {cert.issuer}</span> : ''}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </section>
                            )}

                            {/* skills */}
                            {skills.length > 0 && (
                                <section>
                                    <SectionTitle>Skills</SectionTitle>
                                    <div className="flex flex-wrap gap-1.5">
                                        {skills.slice(0, 48).map((s, i) => (
                                            <span key={`${s}-${i}`} className="rounded-full bg-[var(--surface-tertiary)] px-2 py-0.5 text-[12px] text-[var(--text-secondary)]">
                                                {s}
                                            </span>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {!loading && !c.summary && !c.experience?.length && !c.education?.length && skills.length === 0 && (
                                <div className="flex items-center gap-2 py-4 text-[13px] text-[var(--text-muted)]">
                                    <Building2 size={15} /> No further detail on file.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ════ RIGHT: tabs ════ */}
                    <div className="flex min-w-0 flex-col" style={{ flexBasis: '45%' }}>
                        <div className="flex shrink-0 items-center gap-1 p-2">
                            <div className="flex w-full gap-1 rounded-lg bg-[var(--surface-secondary)] p-1">
                                {(
                                    [
                                        ['criteria', 'Criteria'],
                                        ['resumes', 'Résumé(s)'],
                                        ['applications', 'Applications'],
                                    ] as [TabId, string][]
                                ).map(([id, label]) => (
                                    <button
                                        key={id}
                                        type="button"
                                        onClick={() => setTab(id)}
                                        className={`flex-1 rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors ${
                                            tab === id
                                                ? 'bg-[var(--surface-primary)] text-[#0d1830] shadow-sm'
                                                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                                        }`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="min-h-0 flex-1 overflow-y-auto p-4">
                            {tab === 'criteria' && <CriteriaTab c={c} criteria={criteria} criteriaLabels={criteriaLabels} />}
                            {tab === 'resumes' && (
                                <ResumesTab contactId={summary.id} history={history} loading={historyLoading} latest={resume} latestState={resumeState} />
                            )}
                            {tab === 'applications' && <ApplicationsTab history={history} loading={historyLoading} />}
                        </div>
                    </div>
                </div>
            </aside>
        </div>
    );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
    return <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">{children}</h3>;
}

function StatCard({ k, v }: { k: string; v: string }) {
    return (
        <div className="flex flex-col gap-0.5 rounded-lg bg-[var(--surface-secondary)] px-3 py-2">
            <span className="text-[11px] text-[var(--text-tertiary)]">{k}</span>
            <span className="text-[13px] font-medium text-[var(--text-primary)]">{v}</span>
        </div>
    );
}

function ExperienceGroup({ group }: { group: { company?: string; domain?: string; roles: ExperienceEntry[] } }) {
    if (group.roles.length === 1) {
        const e = group.roles[0]!;
        return (
            <div className="flex items-start gap-3">
                <CompanyLogo name={group.company || ''} src={favicon(group.domain)} size={34} />
                <RoleBody e={e} showCompany />
            </div>
        );
    }
    const total = group.roles.reduce((s, e) => s + monthsOf(e), 0);
    return (
        <div>
            <div className="mb-3 flex items-center gap-3">
                <CompanyLogo name={group.company || ''} src={favicon(group.domain)} size={34} />
                <div className="min-w-0">
                    <span className="block truncate text-[14px] font-semibold text-[var(--text-primary)]">{group.company}</span>
                    <span className="block text-[12px] text-[var(--text-tertiary)]">{ym(total)}</span>
                </div>
            </div>
            <div className="flex flex-col gap-3 border-l border-[var(--border-muted)] pl-4">
                {group.roles.map((e, i) => (
                    <RoleBody key={i} e={e} />
                ))}
            </div>
        </div>
    );
}

function RoleBody({ e, showCompany }: { e: ExperienceEntry; showCompany?: boolean }) {
    return (
        <div className="flex w-full flex-col gap-0.5">
            <div className="flex items-center gap-2">
                <span className="text-[14px] font-medium text-[var(--text-primary)]">{e.position_title || 'Role'}</span>
                <span className="flex-1" />
                <span className="whitespace-nowrap text-[12px] text-[var(--text-tertiary)]">{fmtRange(e)}</span>
            </div>
            {showCompany && (
                <div className="flex items-center gap-2 text-[13px]">
                    <span className="min-w-0 flex-1 truncate text-[var(--text-secondary)]">{e.company_name}</span>
                    <span className="text-[12px] text-[var(--text-tertiary)]">{ym(monthsOf(e))}</span>
                </div>
            )}
            {e.company_industry && <span className="text-[12px] text-[var(--text-tertiary)]">{e.company_industry}</span>}
            {e.location && <span className="text-[12px] text-[var(--text-tertiary)]">{e.location}</span>}
            {e.description && (
                <div className="mt-0.5">
                    <ReadMore text={e.description} />
                </div>
            )}
        </div>
    );
}

/** LinkedIn-style collapsible text: clamps to 3 lines with a "Read more" / "Hide" toggle. */
function ReadMore({ text, className }: { text: string; className?: string }) {
    const [open, setOpen] = useState(false);
    const long = text.length > 180 || text.split('\n').length > 3;
    return (
        <>
            <p className={`whitespace-pre-line ${open ? '' : 'line-clamp-3'} ${className ?? 'text-[12px] leading-relaxed text-[var(--text-tertiary)]'}`}>{text}</p>
            {long && (
                <button type="button" onClick={() => setOpen((o) => !o)} className="mt-0.5 text-[12px] font-medium text-[#4f46e5] hover:underline">
                    {open ? 'Hide' : 'Read more'}
                </button>
            )}
        </>
    );
}

// ── RIGHT pane tabs ──

function CriteriaTab({ c, criteria, criteriaLabels }: { c: CandidateResult; criteria: string[]; criteriaLabels: string[] }) {
    const results = c.criteria_results ?? [];
    if (!criteria.length || results.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-[13px] text-[var(--text-muted)]">
                <Sparkles size={20} className="text-[var(--text-tertiary)]" />
                <span>No criteria scored for this candidate.</span>
                <span className="text-[12px]">Add must-haves and enable scoring to see a per-criterion read.</span>
            </div>
        );
    }
    const rate = c.match_rate ?? null;
    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3 rounded-lg border border-[var(--border-muted)] bg-[var(--surface-secondary)] p-3">
                <MatchRing rate={rate} />
                <div className="min-w-0">
                    <div className="text-[12px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">Match rate</div>
                    {c.criteria_summary ? (
                        <p className="mt-0.5 text-[12px] leading-snug text-[var(--text-secondary)]">{c.criteria_summary}</p>
                    ) : (
                        <p className="mt-0.5 text-[12px] text-[var(--text-muted)]">Weighted across {criteria.length} criteria (earlier = higher weight).</p>
                    )}
                </div>
            </div>
            <div className="flex flex-col gap-2">
                {criteria.map((cr, i) => {
                    const res = results[i];
                    const v = VERDICT[res?.verdict ?? 'no'];
                    const Icon = v.icon;
                    return (
                        <div key={i} className="rounded-lg border border-[var(--border-muted)] bg-[var(--surface-primary)] p-3">
                            <div className="flex items-center gap-2">
                                <Icon size={15} style={{ color: v.color }} />
                                <span className="text-[12px] font-semibold" style={{ color: v.color }}>
                                    {v.label}
                                </span>
                                {criteriaLabels[i] && (
                                    <span className="rounded-full bg-[var(--surface-tertiary)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--text-tertiary)]">
                                        {criteriaLabels[i]}
                                    </span>
                                )}
                            </div>
                            <p className="mt-1 text-[12.5px] leading-snug text-[var(--text-secondary)]">{cr.replace(/^the candidate /i, '').replace(/\.$/, '')}</p>
                            {res?.explanation && <p className="mt-1 text-[12px] leading-snug text-[var(--text-tertiary)]">{res.explanation}</p>}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function MatchRing({ rate }: { rate: number | null }) {
    const pct = rate ?? 0;
    const tone = pct >= 75 ? '#2e8b4f' : pct >= 40 ? '#d4a84b' : '#d14b4b';
    return (
        <div
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-[13px] font-semibold"
            style={{ background: `conic-gradient(${tone} ${pct * 3.6}deg, var(--surface-tertiary) 0deg)`, color: 'var(--text-primary)' }}
        >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--surface-primary)]">{rate != null ? `${pct}` : '—'}</span>
        </div>
    );
}

/** Classify a résumé link so we can pick the right inline viewer. */
function resumeFileType(link: ResumeLink): 'pdf' | 'docx' | 'other' {
    const fromExt = (link.ext || '').toLowerCase().replace(/^\./, '');
    const fromUrl = link.url.toLowerCase().split(/[?#]/)[0].match(/\.([a-z0-9]+)$/)?.[1] ?? '';
    const ext = fromExt || fromUrl;
    if (ext === 'pdf') return 'pdf';
    if (ext === 'doc' || ext === 'docx') return 'docx';
    return 'other';
}

/**
 * Inline .docx reader. Browsers can render PDFs in an <iframe> but not Word docs,
 * so we fetch the bytes same-origin (apps/server proxies S3 — the bucket has no
 * CORS rule for our origin) and convert to HTML with mammoth. Formatting is
 * simplified to clean semantic HTML, which reads well for résumés.
 */
function DocxViewer({ fetcher, className }: { fetcher: () => Promise<ArrayBuffer>; className?: string }) {
    const [html, setHtml] = useState<string | null>(null);
    const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

    useEffect(() => {
        let alive = true;
        setState('loading');
        setHtml(null);
        (async () => {
            try {
                const [{ default: mammoth }, buffer] = await Promise.all([import('mammoth/mammoth.browser'), fetcher()]);
                const { value } = await mammoth.convertToHtml({ arrayBuffer: buffer });
                if (alive) {
                    setHtml(value);
                    setState('ready');
                }
            } catch {
                if (alive) setState('error');
            }
        })();
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (state === 'loading') {
        return (
            <div className={`flex items-center justify-center gap-2 text-[12px] text-[var(--text-muted)] ${className ?? ''}`}>
                <Loader2 size={13} className="animate-spin" /> Rendering document…
            </div>
        );
    }
    if (state === 'error') {
        return (
            <div className={`flex items-center justify-center text-[12px] text-[var(--text-muted)] ${className ?? ''}`}>
                Couldn’t render this document. Use “Open” to download it.
            </div>
        );
    }
    return (
        <div className={`overflow-auto bg-white ${className ?? ''}`}>
            <div className="docx-render mx-auto max-w-[720px] px-6 py-5 text-[13px] leading-relaxed text-[var(--text-primary)]" dangerouslySetInnerHTML={{ __html: html ?? '' }} />
        </div>
    );
}

/** Open a presigned résumé URL in a new tab without tripping the popup blocker. */
function openResume(fetcher: () => Promise<ResumeLink>) {
    const w = window.open('', '_blank');
    fetcher()
        .then(({ url }) => {
            if (w) w.location.href = url;
            else window.open(url, '_blank');
        })
        .catch(() => {
            if (w) w.close();
        });
}

function ResumesTab({
    contactId,
    history,
    loading,
    latest,
    latestState,
}: {
    contactId: string;
    history: CandidateHistory | null;
    loading: boolean;
    latest: ResumeLink | null;
    latestState: 'loading' | 'ready' | 'none';
}) {
    const appsWithResume = (history?.applications ?? []).filter((a) => a.resume);

    return (
        <div className="flex flex-col gap-3">
            {/* latest résumé */}
            <div className="rounded-lg border border-[var(--border-muted)] bg-[var(--surface-secondary)] p-3">
                <div className="mb-2 flex items-center justify-between">
                    <span className="text-[12px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">Latest résumé</span>
                    {latestState === 'ready' && latest && (
                        <button
                            type="button"
                            onClick={() => openResume(() => getResumeUrl(contactId))}
                            className="inline-flex items-center gap-1 text-[12px] text-[#0d1830] hover:underline"
                        >
                            <ExternalLink size={12} /> Open
                        </button>
                    )}
                </div>
                {latestState === 'loading' && (
                    <div className="flex items-center gap-2 py-6 text-[12px] text-[var(--text-muted)]">
                        <Loader2 size={13} className="animate-spin" /> Loading résumé…
                    </div>
                )}
                {latestState === 'none' && <div className="py-6 text-center text-[12px] text-[var(--text-muted)]">No résumé on file.</div>}
                {latestState === 'ready' && latest && (
                    resumeFileType(latest) === 'docx' ? (
                        <DocxViewer
                            fetcher={() => getResumeBytes(contactId)}
                            className="h-[460px] w-full rounded-md border border-[var(--border-muted)]"
                        />
                    ) : (
                        <iframe
                            title="Résumé preview"
                            src={latest.url}
                            className="h-[460px] w-full rounded-md border border-[var(--border-muted)] bg-white"
                        />
                    )
                )}
            </div>

            {/* per-application résumés */}
            {!loading && appsWithResume.length > 0 && (
                <div>
                    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">By application</div>
                    <div className="flex flex-col gap-1.5">
                        {appsWithResume.map((a) => (
                            <button
                                key={a.opportunityId}
                                type="button"
                                onClick={() => openResume(() => getOppResumeUrl(a.opportunityId))}
                                className="flex items-center gap-2 rounded-md border border-[var(--border-muted)] bg-[var(--surface-primary)] px-3 py-2 text-left text-[12px] hover:bg-[var(--surface-secondary)]"
                            >
                                <FileText size={13} className="shrink-0 text-[var(--text-tertiary)]" />
                                <span className="min-w-0 flex-1 truncate text-[var(--text-secondary)]">{a.job?.title || '(role not recorded)'}</span>
                                <span className="text-[11px] text-[var(--text-tertiary)]">{(a.resume?.ext || 'file').replace(/^\./, '').toUpperCase()} ↗</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Applications tab: live Lever recruiting history ──

function stageTone(a: ApplicationEntry): string {
    const s = (a.currentStage || '').toLowerCase();
    if (a.archived) return 'bg-[var(--surface-tertiary)] text-[var(--fg-danger)]';
    if (/offer|hired/.test(s)) return 'bg-[var(--surface-tertiary)] text-[#1c2b4d]';
    if (/interview|onsite|screen|assess|debrief/.test(s)) return 'bg-[var(--surface-tertiary)] text-[var(--text-primary)]';
    return 'bg-[var(--surface-tertiary)] text-[var(--text-tertiary)]';
}

function ApplicationsTab({ history, loading }: { history: CandidateHistory | null; loading: boolean }) {
    const apps = history?.applications ?? [];
    if (loading) return <div className="py-6 text-[12px] text-[var(--text-muted)]">Loading recruiting history…</div>;
    if (apps.length === 0) {
        return (
            <div className="py-16 text-center text-[12px] text-[var(--text-muted)]">
                {history?.unavailable ? 'Recruiting history unavailable.' : 'No applications on record in the ATS.'}
            </div>
        );
    }
    return (
        <div className="flex flex-col gap-3">
            <div className="flex gap-4 text-[12px] text-[var(--text-tertiary)]">
                <span>
                    <b className="text-[var(--text-primary)]">{history?.summary.applications ?? apps.length}</b> applications
                </span>
                <span>
                    <b className="text-[var(--text-primary)]">{history?.summary.interviews ?? 0}</b> interviews
                </span>
                <span>
                    <b className="text-[var(--text-primary)]">{history?.summary.offers ?? 0}</b> offers
                </span>
                <span>
                    <b className="text-[var(--text-primary)]">{history?.summary.notes ?? 0}</b> notes
                </span>
            </div>
            <div className="flex flex-col gap-2">
                {apps.map((a, i) => (
                    <ApplicationCard key={a.opportunityId} a={a} defaultOpen={i === 0} />
                ))}
            </div>
        </div>
    );
}

function ApplicationCard({ a, defaultOpen }: { a: ApplicationEntry; defaultOpen: boolean }) {
    const [open, setOpen] = useState(defaultOpen);
    const sub = [
        a.job ? [a.job.department, a.job.team].filter(Boolean).join(' / ') : '',
        a.job?.location,
        a.appliedAt ? `applied ${d8(a.appliedAt)}` : '',
        a.source,
    ]
        .filter(Boolean)
        .join(' · ');
    const qa = a.application?.questions ?? [];
    const hasDetail =
        qa.length || (a.notes?.length ?? 0) || (a.interviews?.length ?? 0) || (a.feedback?.length ?? 0) || a.offer || (a.stageHistory?.length ?? 0) || a.resume;

    return (
        <div className="rounded-lg border border-[var(--border-muted)] bg-[var(--surface-secondary)] p-3">
            <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-start gap-2 text-left">
                <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium text-[var(--text-primary)]">{a.job?.title || '(role not recorded)'}</div>
                    {sub && <div className="text-[11px] text-[var(--text-tertiary)]">{sub}</div>}
                </div>
                {a.interviewScore != null && (
                    <span className="inline-flex items-center gap-0.5 rounded-full bg-[var(--surface-tertiary)] px-2 py-0.5 text-[11px] font-semibold text-[#1c2b4d]">
                        <Star size={10} /> {a.interviewScore}/4
                    </span>
                )}
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${stageTone(a)}`}>
                    {a.currentStage || '—'}
                    {a.archived ? ' · archived' : ''}
                </span>
                {hasDetail ? <ChevronRight size={15} className={`mt-0.5 shrink-0 text-[var(--text-tertiary)] transition-transform ${open ? 'rotate-90' : ''}`} /> : null}
            </button>

            {a.stageHistory && a.stageHistory.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-1 text-[11px] text-[var(--text-tertiary)]">
                    {a.stageHistory.map((s, i) => (
                        <span key={i} title={`${d8(s.at)}${s.by ? ` · ${s.by}` : ''}`}>
                            {i ? <span className="mx-0.5 text-[var(--border-default)]">→</span> : null}
                            {s.stage || '?'}
                        </span>
                    ))}
                </div>
            )}

            {open && hasDetail ? (
                <div className="mt-2.5 flex flex-col gap-2.5 border-t border-[var(--border-muted)] pt-2.5">
                    {a.resume && (
                        <HistoryRow label="Résumé">
                            <button
                                type="button"
                                onClick={() => openResume(() => getOppResumeUrl(a.opportunityId))}
                                className="inline-flex items-center gap-1 text-[#0d1830] hover:underline"
                            >
                                <FileText size={12} /> open {(a.resume.ext || 'file').replace(/^\./, '').toUpperCase()} ↗
                            </button>
                        </HistoryRow>
                    )}
                    {qa.length > 0 && (
                        <HistoryRow label="Application">
                            <div className="flex flex-col gap-1">
                                {qa.slice(0, 12).map((q, i) => (
                                    <div key={i}>
                                        <span className="font-medium text-[var(--text-primary)]">{q.q}</span> <span className="text-[var(--text-secondary)]">{q.a}</span>
                                    </div>
                                ))}
                            </div>
                        </HistoryRow>
                    )}
                    {(a.interviews?.length ?? 0) > 0 && (
                        <HistoryRow label="Interviews">
                            {a.interviews!.map((iv, i) => (
                                <div key={i}>
                                    {iv.subject || iv.stage || 'Interview'}
                                    {iv.date ? ` · ${d8(iv.date)}` : ''}
                                    {iv.interviewers?.length ? ` · ${iv.interviewers.join(', ')}` : ''}
                                </div>
                            ))}
                        </HistoryRow>
                    )}
                    {(a.feedback?.length ?? 0) > 0 && (
                        <HistoryRow label="Feedback">
                            {a.feedback!.map((f, i) => (
                                <div key={i}>
                                    {f.author || '?'}
                                    {f.score != null ? ` · ★ ${f.score}/4` : f.completed ? ' · completed' : ' · pending'}
                                    {f.fields?.length ? ` · ${f.fields.slice(0, 4).map((x) => `${x.q}: ${x.value}`).join('; ')}` : ''}
                                </div>
                            ))}
                        </HistoryRow>
                    )}
                    {a.offer && (
                        <HistoryRow label="Offer">
                            {a.offer.status}
                            {a.offer.sentAt ? ` · sent ${d8(a.offer.sentAt)}` : ''}
                            {a.offer.fields?.length ? (
                                <div className="mt-0.5">{a.offer.fields.slice(0, 8).map((x) => `${x.label}: ${x.value}`).join(' · ')}</div>
                            ) : null}
                        </HistoryRow>
                    )}
                    {(a.notes?.length ?? 0) > 0 && (
                        <HistoryRow label="Notes">
                            <div className="flex flex-col gap-1.5">
                                {a.notes!.map((n, i) => (
                                    <div key={i} className="rounded-md bg-[var(--surface-primary)] px-2 py-1.5">
                                        <div className="text-[10px] text-[var(--text-tertiary)]">
                                            {n.author || '?'}
                                            {n.at ? ` · ${d8(n.at)}` : ''}
                                            {n.stage ? ` · ${n.stage}` : ''}
                                            {n.secret ? ' · internal' : ''}
                                        </div>
                                        <div className="text-[var(--text-secondary)]">{n.text}</div>
                                    </div>
                                ))}
                            </div>
                        </HistoryRow>
                    )}
                </div>
            ) : null}
        </div>
    );
}

function HistoryRow({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="grid grid-cols-[72px_1fr] gap-2 text-[12px]">
            <span className="text-[10px] uppercase tracking-wide text-[var(--text-tertiary)]">{label}</span>
            <div className="text-[var(--text-secondary)]">{children}</div>
        </div>
    );
}
